import type { FieldError, FieldPath, FieldValues, UseFormRegister, UseFormSetValue, PathValue } from 'react-hook-form'
import type { HealthFormData } from '@/app/schemas/health-comparator/health-form.schema'
import AppFormInput from '@/app/components/atomic/AppFormInput'
import AppIcon from '@/app/components/atomic/AppIcon'
import { formatDateInput } from '@/app/utils/helpers'
import { dateToFrDate, maxBirthDateForAge, minBirthDateForAge } from '@/app/utils/health/health-date-utils'
import dynamic from 'next/dynamic'

const AppDatePicker = dynamic(() => import('@/app/components/atomic/molecules/AppDatePicker'))

interface DateFieldProps<T extends FieldValues> {
    name: FieldPath<T>
    /** Id de l'input, à faire correspondre au htmlFor du label appelant. */
    id?: string
    register: UseFormRegister<T>
    setValue: UseFormSetValue<T>
    error?: FieldError
    dataTest: string
    autoComplete?: string
    /** Render the V2 calendar picker instead of the text mask. */
    usePicker?: boolean
    /** Current value (dd/MM/yyyy) — used to seed the picker when usePicker is true. */
    value?: string
    /** Minimum age in years — bounds the picker's latest selectable date (0 caps at today). */
    minAge?: number
    /** Maximum age in years — bounds the picker's earliest selectable date. */
    maxAge?: number
    /** Disallow past dates — bounds the picker's earliest selectable date at today. */
    notInPast?: boolean
    /** Explicit earliest selectable date (dd/MM/yyyy) — overrides the age/notInPast bounds. */
    minDate?: string
    /** Explicit latest selectable date (dd/MM/yyyy) — overrides the age/notInPast bounds. */
    maxDate?: string
    /** Date-of-birth field — guides the picker year → month → day. */
    birthdate?: boolean
    /** Beige/neutral picker theme, for coherence with the date-of-birth pickers. */
    beige?: boolean
    pickerVariant?: 'v2' | 'travel'
    placeholder?: string
}

export default function DateField<T extends FieldValues = HealthFormData>({
    name,
    id,
    register,
    setValue,
    error,
    dataTest,
    autoComplete = 'off',
    usePicker = false,
    value = '',
    minAge,
    maxAge,
    notInPast = false,
    minDate,
    maxDate,
    birthdate = false,
    beige = false,
    pickerVariant = 'v2',
    placeholder = 'JJ/MM/AAAA',
}: DateFieldProps<T>) {
    const commitDate = (next: string) => {
        setValue(name, next as PathValue<T, typeof name>, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        })
    }

    if (usePicker) {
        const computedMaxDate = minAge != null ? maxBirthDateForAge(minAge) : undefined
        const computedMinDate = maxAge != null
            ? minBirthDateForAge(maxAge)
            : notInPast
                ? dateToFrDate(new Date())
                : undefined

        return (
            <div data-test={dataTest}>
                <AppDatePicker
                    id={id}
                    variant={pickerVariant}
                    value={value}
                    onChange={commitDate}
                    error={error}
                    displayIcon
                    placeholder={placeholder}
                    maxDate={maxDate ?? computedMaxDate}
                    minDate={minDate ?? computedMinDate}
                    birthdate={birthdate}
                    beige={beige}
                />
            </div>
        )
    }

    const field = register(name)

    return (
        <AppFormInput
            label=""
            variant="health"
            type="text"
            placeholder={placeholder}
            maxLength={10}
            inputMode="numeric"
            autoComplete={autoComplete}
            icon={<AppIcon iconClassName="fa-calendar" type="regular" />}
            data-test={dataTest}
            {...field}
            onChange={(e) => commitDate(formatDateInput(e.target.value))}
            error={error}
        />
    )
}
